import axios from 'axios';
import createSignedRequest from './createSignedRequest';

const { apiURL } = require('./apiURL');

/**
 * Signed call to a commercial-accounts endpoint. Resolves `{ ok: true, data }` or
 * `{ ok: false, error }` where `error` is the backend code (e.g. `forbidden`, `invalid_bank_details`).
 * @param {string} method
 * @param {string} path
 * @param {object} [body]
 * @returns {Function}
 */
const call = (method, path, body) => async (dispatch, getState) => {
  const org = getState().currentOrganisation;
  try {
    const res = await axios(
      await createSignedRequest(method, `${apiURL}${path}`, body ? JSON.stringify(body) : null, {
        Organisation: String(org || ''),
      })
    );
    return { ok: true, data: res.data };
  } catch (e) {
    return { ok: false, error: e?.response?.data?.error || e.message, field: e?.response?.data?.field };
  }
};

const enc = encodeURIComponent;

/** Build path with optional query string (skips null/undefined/empty). */
const withQuery = (path, params = {}) => {
  const qs = Object.entries(params || {})
    .filter(([, v]) => v != null && v !== '')
    .map(([k, v]) => `${enc(k)}=${enc(String(v))}`)
    .join('&');
  return qs ? `${path}?${qs}` : path;
};

/** Partner Admin / Platform Admin: the Partner's client accounts and accepted fee schedule (R-7028). */
const listPartnerAccounts = (partnerOrgId) =>
  call('GET', `/my/partner-accounts${partnerOrgId ? `?partnerOrgId=${enc(partnerOrgId)}` : ''}`);

/** Partner Admin / Platform Admin: set a Partner client's Plan, FTE band and Add-ons (R-7028). */
const setPartnerAccountPlan = (organisationId, { planId, fteBand, addOnIds }) =>
  call('PUT', `/my/partner-accounts/${enc(organisationId)}/plan`, { planId, fteBand, addOnIds });

/** Platform Admin: set or clear the Demo flag on an Organisation. */
const setDemoFlag = (organisationId, demo) => call('PUT', `/admin/organisations/${enc(organisationId)}/demo`, { demo: !!demo });

/** Platform Admin: link an Organisation to an Ambassador with a reason (R-6109). */
const linkAmbassadorCustomer = ({ customerOrgId, ambassadorOrgId, reason, effectiveFrom }) =>
  call('POST', '/admin/ambassador-links', { customerOrgId, ambassadorOrgId, reason, effectiveFrom });

/** Platform Admin: record notice on an Ambassador or Partner Reseller Agreement. */
const recordAgreementNotice = (contractId, { noticeReceivedAt, givenBy }) =>
  call('POST', `/admin/agreements/${enc(contractId)}/notice`, { noticeReceivedAt, givenBy });

/** Ambassador org admin: the Ambassador view (R-6107). */
const getAmbassadorView = () => call('GET', '/my/ambassador');

/** Ambassador org admin: save bank details (R-6106). */
const saveAmbassadorBankDetails = (details) => call('PUT', '/my/ambassador/bank-details', details);

/** Platform Admin: verify an Ambassador's bank details (R-6106). */
const verifyAmbassadorBankDetails = (ambassadorOrgId) =>
  call('POST', `/admin/ambassadors/${enc(ambassadorOrgId)}/bank-details/verify`, {});

/** Platform Admin: mark a Self-Billing Statement paid (R-6105). */
const markAmbassadorStatementPaid = (statementId, paidAt) =>
  call('POST', `/admin/ambassador-statements/${enc(statementId)}/paid`, { paidAt });

/** Platform Admin: Ambassador revenue-share report (R-6108). */
const getAmbassadorReport = (ambassadorOrgIdOrFilters, maybeFilters) => {
  const filters =
    ambassadorOrgIdOrFilters && typeof ambassadorOrgIdOrFilters === 'object'
      ? ambassadorOrgIdOrFilters
      : maybeFilters || {};
  const ambassadorOrgId =
    typeof ambassadorOrgIdOrFilters === 'string' ? ambassadorOrgIdOrFilters : filters.ambassadorOrgId;
  return call(
    'GET',
    withQuery('/admin/ambassadors/report', {
      ambassadorOrgId,
      agreement: filters.agreement,
      status: filters.status,
    })
  );
};

/** I-033: tax profile, self-billing, payout recipient, documents. */
const saveAmbassadorTaxProfile = (profile) => call('PUT', '/my/ambassador/tax-profile', profile);
const validateAmbassadorVat = (vatNumber) => call('POST', '/my/ambassador/vat-validation', { vatNumber });
const acceptSelfBilling = (body) => call('POST', '/my/ambassador/self-billing/accept', body || {});
const listSelfBillingDocuments = () => call('GET', '/my/ambassador/self-billing/documents');
const savePayoutRecipient = (details) => call('PUT', '/my/ambassador/payout-recipient', details);
const confirmPayoutRecipient = (ambassadorOrgId, recipientId) =>
  call('POST', `/admin/ambassadors/${enc(ambassadorOrgId)}/payout-recipient/${enc(recipientId)}/confirm`, {});
const issueSettlementDocument = (settlementId, body) =>
  call('POST', `/admin/ambassador-settlements/${enc(settlementId)}/document`, body || {});
const prepareDocumentPayout = (documentId) =>
  call('POST', `/admin/ambassador-documents/${enc(documentId)}/prepare-payout`, {});
const exportSelfBilling = (ambassadorOrgId, month) =>
  call('GET', `/admin/ambassadors/${enc(ambassadorOrgId)}/self-billing/export${month ? `?month=${enc(month)}` : ''}`);

/** Platform Admin: payout cycles (documents + Wise instructions) for Ambassadors finance tab. */
const listAmbassadorPayouts = () => call('GET', '/admin/ambassador-payouts');

/** Platform Admin: Ambassador Overview (R-6119). */
const getAmbassadorOverview = (ambassadorOrgId) =>
  call('GET', `/admin/ambassadors/${enc(ambassadorOrgId)}/overview`);

/** Platform Admin: engagement worklist (R-6121). */
const getAmbassadorEngagementQueue = (filters = {}) =>
  call('GET', withQuery('/admin/ambassadors/engagement-queue', { status: filters.status }));

/** Platform Admin: update touch log / next follow-up (R-6121). */
const updateAmbassadorEngagement = (ambassadorOrgId, body) =>
  call('PUT', `/admin/ambassadors/${enc(ambassadorOrgId)}/engagement`, body || {});

/** Platform Admin: Trigger email nudge once (R-6122). */
const sendAmbassadorPartnerNudge = (ambassadorOrgId, template) =>
  call('POST', `/admin/ambassadors/${enc(ambassadorOrgId)}/nudge`, { template });

/** Partners hub (R-8050–R-8056). Filters: agreement=all|signed|missing, status=active|all. */
const getPartnersHubOverview = (filters = {}) =>
  call('GET', withQuery('/admin/partners/overview', { agreement: filters.agreement, status: filters.status }));
const getPartnersAmbassadorsProgramme = (filters = {}) =>
  call('GET', withQuery('/admin/partners/ambassadors', { agreement: filters.agreement, status: filters.status }));
const getPartnersWhiteLabelsProgramme = (filters = {}) =>
  call('GET', withQuery('/admin/partners/white-labels', { agreement: filters.agreement, status: filters.status }));
const getPartnersWhiteLabelsWorklist = (filters = {}) =>
  call('GET', withQuery('/admin/partners/white-labels/worklist', { status: filters.status }));
const getPartnersWhiteLabelFirm = (whiteLabelOrgId) =>
  call('GET', `/admin/partners/white-labels/${enc(whiteLabelOrgId)}`);
const getPartnersConsultantsProgramme = (filters = {}) =>
  call('GET', withQuery('/admin/partners/consultants', { agreement: filters.agreement, status: filters.status }));
const getPartnersConsultantsWorklist = (filters = {}) =>
  call('GET', withQuery('/admin/partners/consultants/worklist', { status: filters.status }));
const getPartnersConsultantFirm = (consultantOrgId) =>
  call('GET', `/admin/partners/consultants/${enc(consultantOrgId)}`);

const Actions = {
  listPartnerAccounts,
  setPartnerAccountPlan,
  setDemoFlag,
  linkAmbassadorCustomer,
  recordAgreementNotice,
  getAmbassadorView,
  saveAmbassadorBankDetails,
  verifyAmbassadorBankDetails,
  markAmbassadorStatementPaid,
  getAmbassadorReport,
  saveAmbassadorTaxProfile,
  validateAmbassadorVat,
  acceptSelfBilling,
  listSelfBillingDocuments,
  savePayoutRecipient,
  confirmPayoutRecipient,
  issueSettlementDocument,
  prepareDocumentPayout,
  exportSelfBilling,
  listAmbassadorPayouts,
  getAmbassadorOverview,
  getAmbassadorEngagementQueue,
  updateAmbassadorEngagement,
  sendAmbassadorPartnerNudge,
  getPartnersHubOverview,
  getPartnersAmbassadorsProgramme,
  getPartnersWhiteLabelsProgramme,
  getPartnersWhiteLabelsWorklist,
  getPartnersWhiteLabelFirm,
  getPartnersConsultantsProgramme,
  getPartnersConsultantsWorklist,
  getPartnersConsultantFirm,
};

export { Actions };
